import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import Icon from "./Icon";
import UserAvatar from "./UserAvatar";
import Currency, { ConvertCurrency } from "../helpers/Currency/Currency";
import Badge from "./Badge";
import BackToAdmin from "../helpers/BackToAdmin/BackToAdmin";
import { convertRoleToPath, convertRoleToText } from "../utils/enum";

const Header = ({ sidebarControl, sidebarActive, mobileMenuControl }) => {
  const title = useSelector((state) => state.title);

  return (
    <div className="navbar-header">
      <div className="row align-items-center justify-content-between">
        <div className="col-auto">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={sidebarControl}
            >
              <Icon
                icon={
                  sidebarActive
                    ? "solar:close-circle-broken"
                    : "solar:hamburger-menu-broken"
                }
                className="icon text-2xxl non-active"
              />
            </button>
            <button
              onClick={mobileMenuControl}
              type="button"
              className="sidebar-mobile-toggle"
            >
              <Icon
                icon="solar:hamburger-menu-broken"
                className="icon text-2xxl non-active"
              />
            </button>
            <span className="text-xl fw-bold text-nowrap text-line-1 d-none d-sm-block">
              {title.title}
            </span>
          </div>
        </div>
        <div className="col-auto me-auto">
          <HeaderOperator />
        </div>
      </div>
    </div>
  );
};

export default Header;

export const HeaderOperator = () => {
  return (
    <div className="d-flex flex-wrap align-items-center gap-3">
      <BackToAdmin />
      <ThemeToggleButton />
      <ProfileDropdown />
    </div>
  );
};

export const ThemeToggleButton = ({ isSwitch }) => {
  // 1. Initialize state for the current theme
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");

  // 2. Function to update the theme on the HTML element
  const updateThemeOnHtmlEl = (theme) => {
    document.documentElement.setAttribute("data-theme", theme);
  };

  // 4. On initial render, set the theme from localStorage
  useEffect(() => {
    updateThemeOnHtmlEl(theme);
  }, [theme]);

  // 5. Toggle theme when button is clicked
  const handleThemeToggle = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    updateThemeOnHtmlEl(newTheme);
  };

  if (isSwitch)
    return (
      <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative mb-16">
        <label
          htmlFor="theme"
          className="position-absolute w-100 h-100 start-0 top-0"
        />
        <div className="d-flex align-items-center gap-3 justify-content-between">
          <span className="form-check-label line-height-1 fw-medium text-secondary-light">
            حالت تاریک
          </span>
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="theme"
            checked={theme === "dark"}
            onChange={handleThemeToggle}
          />
        </div>
      </div>
    );
  return (
    <button
      type="button"
      data-theme-toggle
      className="w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center"
      onClick={handleThemeToggle}
    >
      <Icon icon="solar:sun-broken" className="text-primary-light text-xl" />
    </button>
  );
};

const ProfileDropdown = () => {
  const user = useSelector((state) => state.user);

  return (
    <div className="dropdown">
      <button
        className="d-flex justify-content-center align-items-center rounded-circle"
        type="button"
        data-bs-toggle="dropdown"
      >
        <UserAvatar
          {...(user?.avatar
            ? { image: user?.avatar }
            : {
                icon: "solar:user-broken",
              })}
        />
      </button>
      <div className="dropdown-menu to-top dropdown-menu-sm">
        <div className="py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
          <div className="text-start">
            <h6 className="text-lg text-primary-light fw-semibold mb-2">
              {user.firstName} {user.lastName}
            </h6>
            <span className="text-secondary-light fw-medium text-sm">
              {convertRoleToText(user?.role)}
            </span>
          </div>
          <div>
            <UserAvatar
              {...(user?.avatar
                ? { image: user?.avatar }
                : {
                    icon: "solar:user-broken",
                  })}
            />
          </div>
        </div>
        <hr />
        <ul className="to-top-list">
          <li>
            <Link
              className="dropdown-item text-black p-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3"
              to={`/${convertRoleToPath(user?.role)}/profile`}
            >
              <Icon icon="solar:user-broken" className="icon text-xl" />
              ویرایش حساب کاربری
            </Link>
          </li>
          <li>
            <Link
              className="dropdown-item text-black p-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3"
              to={`/${convertRoleToPath(user?.role)}/profile/changed-password`}
            >
              <Icon
                icon="solar:pen-new-square-broken"
                className="icon text-xl"
              />
              ویرایش کلمه عبور
            </Link>
          </li>
          <li>
            <Link
              className="dropdown-item text-black p-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3"
              to={`/${convertRoleToPath(user?.role)}/profile/settings`}
            >
              <Icon icon="solar:settings-broken" className="icon text-xl" />
              تنظیمات حساب کاربری
            </Link>
          </li>
          <li>
            <Link
              className="dropdown-item text-black p-8 border border-danger-200 bg-danger-50 bg-hover-danger-100 radius-12 d-flex align-items-center gap-3"
              to="/auth/logout"
            >
              <Icon icon="solar:logout-3-broken" className="icon text-xl" />{" "}
              خروج از حساب کاربری
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
};
